// @ts-check
import { ref } from 'vue';
import Axios from 'axios';
import { captureSentryMessage } from '@/lib/sentry';

import HTTP from '@/utils/http';
import Meta from '@/store/modules/models/global/Meta';

/**
 * List api results
 *
 * @param {string} url api to query
 * @param {import('zod').ZodSchema} resultsModel Zod to parse list items
 * @param {import('zod').ZodSchema} metaModel Zod to parse list meta
 * @param {object} defaultParams Params to use on every request
 *
 * @returns {[typeof fetch, typeof results, typeof meta, typeof isLoading, typeof refresh]}
 */
export default function useList(
  url,
  resultsModel,
  metaModel = Meta,
  defaultParams = {},
) {
  const isLoading = ref(false);
  const results = ref([]);
  const cancelSource = ref(Axios.CancelToken.source());
  const meta = ref(null);
  const lastParams = ref(null);

  async function fetch(params = {}) {
    lastParams.value = params;
    isLoading.value = true;
    // cancel the in flight request
    cancelSource.value.cancel('wait');
    // refresh cancel token for this request
    cancelSource.value = Axios.CancelToken.source();

    // make the request
    try {
      const response = await HTTP({
        url,
        params: { ...defaultParams, ...params },
        cancelToken: cancelSource.value.token,
      });

      // results!!!
      results.value = response.data.data
        .map((item) => {
          const parsedItem = resultsModel.safeParse(item);
          if (parsedItem.success === true) {
            return parsedItem.data;
          }

          captureSentryMessage('Item failed zod validation', (scope) => {
            scope.setExtra('url', url);
            scope.setExtra('error', parsedItem.error);
          });

          return undefined;
        })
        .filter(item => item);

      const parsedMeta = metaModel.safeParse(response.data.meta);
      meta.value = parsedMeta.success ? parsedMeta.data : null;

      isLoading.value = false;
    } catch (error) {
      // still searching
      if (error.message === 'wait') {
        return;
      }

      // re-throw unexpected errors
      results.value = [];
      meta.value = null;
      isLoading.value = false;
      throw error;
    }
  }

  async function refresh() {
    if (lastParams.value) {
      return fetch(lastParams.value);
    }

    return undefined;
  }

  return [fetch, results, meta, isLoading, refresh];
}
